import { useNavigate } from "react-router-dom"
import { Link } from "@/components/ui/link"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  InvoiceGet,
  InvoiceStatus,
  formatInvoiceNumber,
  useInvoices,
  useInvoicesActions,
} from "@/api/invoices"
import InvoiceBadge from "./invoice.badge"
import { format } from "date-fns"
import ButtonCopy from "@/components/ui/button-copy"
import Loader from "@/components/layout/loader"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"
import OptionsDropdownMenu from "@/components/ux/options.dropdown-menu"
import { toast } from "sonner"
import { TokenIcon } from "@/components/token"
import { getInvoicePDFUrl, getShortUrl } from "@/api/payment-pages"

interface InvoicesTableProps {
  onLinkTransaction: (invoice: InvoiceGet) => void;
}

export default function InvoicesTable({
  onLinkTransaction,
}: InvoicesTableProps) {
  const { invoices, hasMore, size, setSize } = useInvoices()
  const { remove } = useInvoicesActions()
  const navigate = useNavigate()

  const onRemove = async (id: string) => {
    await remove(id)
    toast.success("Invoice removed")
  }

  if (!invoices) {
    return <Loader message="Loading invoices..." />
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="rounded-t-md">
          <TableHead className="pl-6">URL</TableHead>
          <TableHead>Number</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Total</TableHead>
          <TableHead>Status</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invoices.map((invoice) => (
          <TableRow key={invoice.id}>
            <TableCell className="pl-6">
              <div className="font-medium flex items-center gap-4">
                <ButtonCopy
                  className="text-gray-300"
                  value={getShortUrl(invoice.paymentPage.shortId)}
                  data-testid="copy-link"
                />
                <Link
                  to={`/invoices/${invoice.id}`}
                  className="hidden sm:block"
                >
                  {invoice.paymentPage.shortId}
                </Link>
              </div>
            </TableCell>
            <TableCell>#{formatInvoiceNumber(invoice.number)}</TableCell>
            <TableCell>
              {invoice.customer.firstName} {invoice.customer.lastName}
            </TableCell>
            <TableCell>
              {format(new Date(invoice.date), "MMM do, yyyy")}
            </TableCell>
            <TableCell>
              <div className="flex flex-row items-center gap-2">
                <TokenIcon name={invoice.token} />
                <span>{invoice.total.toLocaleString()}</span>
                <span>{invoice.token}</span>
              </div>
            </TableCell>
            <TableCell>
              <InvoiceBadge status={invoice.status} />
            </TableCell>
            <TableCell>
              <OptionsDropdownMenu>
                <DropdownMenuItem
                  disabled={invoice.status !== InvoiceStatus.DRAFT}
                  onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={invoice.status !== InvoiceStatus.DRAFT}
                  onClick={() => onRemove(invoice.id)}
                >
                  Remove
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={invoice.status === InvoiceStatus.DRAFT}
                  asChild
                >
                  <a href={getInvoicePDFUrl(invoice.paymentPage.shortId)}>
                    Download PDF
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onLinkTransaction(invoice)}
                  disabled={invoice.status !== InvoiceStatus.PENDING}
                >
                  Link Transaction
                </DropdownMenuItem>
              </OptionsDropdownMenu>
            </TableCell>
          </TableRow>
        ))}
        {invoices.length === 0 && (
          <TableRow>
            <TableCell colSpan={7} className="text-center text-gray-300">
              No invoices found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {hasMore && (
        <TableFooter>
          <TableRow>
            <TableCell colSpan={7}>
              <button
                onClick={() => setSize(size + 1)}
                className="flex flex-col w-full items-center bg-white p-3 text-center"
              >
                Load more...
              </button>
            </TableCell>
          </TableRow>
        </TableFooter>
      )}
    </Table>
  )
}
